import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { api, apiError } from "../../lib/api";
import { Emblem } from "../../components/PublicLayout";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";

const field = "w-full bg-[#141414] border border-[#333] text-[#fdfbf7] rounded-sm px-4 py-3 text-sm focus:border-[#d4af37] placeholder:text-[#666]";

export default function InviteAccept() {
  const { token } = useParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState("checking");
  const [info, setInfo] = useState(null);
  const [f, setF] = useState({ name: "", password: "", phone: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/public/invite/${token}`).then(({ data }) => { setInfo(data); setF((p) => ({ ...p, name: data.name })); setState("ready"); })
      .catch((e) => { setState("invalid"); toast.error(apiError(e.response?.data?.detail)); });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (f.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/public/invite/${token}/accept`, f);
      if (data.requires_login) { toast.success("Account created — please sign in"); nav("/staff-login"); return; }
      await refresh();
      toast.success("Welcome to the team!");
      nav(data.role === "cleaner" ? "/cleaner" : "/admin");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center px-5 py-10 noise-overlay">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8"><Emblem variant="full" className="h-24" /></div>
        <div className="bg-[#111] border border-[#333] rounded-sm p-8" data-testid="invite-accept-card">
          {state === "checking" && <div className="text-center py-6"><Loader2 className="animate-spin text-[#d4af37] mx-auto" size={36} /></div>}
          {state === "invalid" && <>
            <h1 className="font-display text-3xl text-[#fdfbf7]">Invitation unavailable</h1>
            <p className="text-[#a3a3a3] text-sm mt-2">This invitation is invalid, expired or already used. Please ask your manager for a new one.</p>
            <Link to="/" className="text-xs text-[#666] mt-4 inline-block hover:text-[#d4af37]">← Back to website</Link>
          </>}
          {state === "ready" && info && <>
            <div className="flex items-center gap-2 text-[#d4af37]"><ShieldCheck size={18} /><p className="text-[11px] tracking-luxe uppercase">Staff onboarding</p></div>
            <h1 className="font-display text-3xl text-[#fdfbf7] mt-2">Set up your account</h1>
            <p className="text-[#a3a3a3] text-sm mt-1">{info.email} · {info.role.replace(/_/g, " ")}</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <input data-testid="invite-accept-name" placeholder="Full name" className={field} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
              <input data-testid="invite-accept-phone" placeholder="Mobile (optional)" className={field} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
              <input data-testid="invite-accept-password" type="password" placeholder="Choose a password (min 8 chars)" className={field} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
              <button data-testid="invite-accept-submit" disabled={busy} className="btn-gold w-full py-3 rounded-sm">{busy ? "Creating…" : "Create my account"}</button>
            </form>
          </>}
        </div>
      </div>
    </div>
  );
}
